/**
 * A sample landing page that will eventually be editable, generated
 * by AI-guided customer interviews.
 *
 * Inspiration:
 *
 * https://claude.ai/public/artifacts/0bd45b57-3ca8-408f-a3f5-0f90d89ef0a3
 * https://claude.ai/public/artifacts/31e57a0b-8097-4255-a10b-83dea8f214f4
 */

import { defRoute } from '@components/router';
import type { SectionType } from './types';
import * as HeroSection from './hero-section';
import * as FeatureSection from './feature-section';
import * as TestimonialsSection from './testimonials-section';
import * as AboutSection from './about-section';
import * as CTASection from './cta-section';
import * as PageFooterSection from './page-footer-section';
import * as PageHeaderSection from './page-header-section';
import { sampleData } from './sample-data';
import { generateTheme, generateThemeCSS } from './theme';
import { IcoSparkles } from '@components/icons';
import { useState, useMemo } from 'preact/hooks';
import { useDocumentTitle } from 'client/utils/use-document-title';

export const route = defRoute({ Page });

const styles = `
.landing-page {
  color: var(--c-neutral-8);
  h2 {
    color: var(--c-primary-6);
    font-size: 2.5rem;
    font-weight: bold;
    margin-bottom: 1em;
  }

  & > :last-child {
    padding-bottom: 8rem;
  }
}

`;

const sectionRenderers: Record<SectionType, (props: { data: any }) => JSX.Element> = {
  Header: PageHeaderSection.View,
  Hero: HeroSection.View,
  Features: FeatureSection.View,
  Testimonials: TestimonialsSection.View,
  About: AboutSection.View,
  CTA: CTASection.View,
  Footer: PageFooterSection.View,
};

const sectionCSS = [
  PageHeaderSection.css,
  HeroSection.css,
  FeatureSection.css,
  TestimonialsSection.css,
  AboutSection.css,
  CTASection.css,
  PageFooterSection.css,
];

function EditMenu(props: { onShuffle(): void }) {
  return (
    <header class="flex gap-2 rounded-2xl p-4 fixed bottom-4 inset-x-4 shadow-lg z-50 bg-white/75 backdrop-blur-2xl border">
      <button
        class="inline-flex items-center gap-2 border p-1 hover:text-pink-500 transition-all bg-white rounded-md"
        onClick={() => {
          props.onShuffle();
        }}
        title="Shuffle colors"
      >
        <IcoSparkles class="size-5" />
        Shuffle Colors
      </button>
    </header>
  );
}

function Page() {
  const [theme, setTheme] = useState(generateTheme);

  const css = useMemo(() => {
    const rootCSS = `
    :root {
      ${generateThemeCSS(theme)}
    }`;

    return rootCSS + styles + sectionCSS.join('\n');
  }, [theme]);

  useDocumentTitle(['Landing page']);

  return (
    <>
      <div class="landing-page text-lg">
        <style>{css}</style>
        {sampleData.map((s) => {
          const Render = sectionRenderers[s.type];
          return <Render key={s.id} data={s.data} />;
        })}
      </div>
      <EditMenu onShuffle={() => setTheme(generateTheme())} />
    </>
  );
}
